<template>
    <div class="pb-10">
        <v-row dense class="mb-4">
            <v-col cols="12" md="2" />
            <v-col cols="12" md="4">
                <v-autocomplete
                    ref="option"
                    v-model="contract.variantId"
                    :items="variants"
                    item-title="title"
                    item-value="value"
                    closable-chips
                    hide-details
                    clearable
                    :label="$t('product_variant')"
                    density="compact"
                    color="primary"
                    data-cy="variant"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2" />
            <v-col cols="12" sm="3" md="2" class="pb-4">
                <v-radio-group v-model="contract.transferType" :disabled="editMode" density="compact" hide-details>
                    <v-radio
                        v-for="{value, title} in useSharing"
                        :key="value"
                        :label="title"
                        :value="value"
                        color="primary"
                        :data-cy="value"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2" />
            <v-col>
                <div class="text-subtitle-1 pb-1">{{ $t('transfer_duration') }}:</div>
                <v-row dense>
                    <v-col cols="12" sm="3" md="2">
                        <v-text-field
                            v-model="contract.duration"
                            label="(HH:mm)*"
                            variant="outlined"
                            color="primary"
                            density="compact"
                            data-cy="duration"
                            maxlength="5"
                            width="110"
                            :rules="[requiredRule('duration'), timeFormatRule()]"
                            @update:model-value="e => formatTime(e, 'duration')"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('start_end_points') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <InfoStartEndPointsForm v-model="contract" :service-type="contractServiceType.ownTransfer" />
            </v-col>
        </v-row>
    </div>
</template>

<script setup lang="ts">
    import {contractServiceType, type SelectOptionType, Sharing} from '~/utils/types/types'
    import {timeFormatRule, requiredRule} from '~/composables/rules'
    import InfoStartEndPointsForm from '~/components/products/information/InfoStartEndPointsForm.vue'

    const props = defineProps<{
        editMode?: boolean
    }>()

    const contract = defineModel<any>()
    const variants = ref<SelectOptionType[]>([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadVariants()
    })

    async function loadVariants() {
        try {
            const productType = contractServiceType.ownTransfer
            const {productVariants} = await app.$api.productVariants.get({
                productType,
            })
            variants.value = productVariants.map(({id, name}) => ({value: id, title: name}))
        } catch (e) {
            variants.value = []
        }
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        contract.value[key] = formattedValue
    }

    const useSharing = computed(() => {
        const sharingTypes: Sharing[] = [Sharing.Shared, Sharing.Private]
        return sharingTypes.map(value => ({value, title: t(`sharingType.${value}`)}))
    })
</script>
